import React, { useEffect, useState } from 'react';

const Audit = () => {
    const [animate, setAnimate] = useState(false);

    useEffect(() => {
        setAnimate(true);
    }, []);

    const auditFeatures = [
        {
            title: "Immutable History",
            desc: "Every batch movement, ingredient change, and quality check is cryptographically logged and locked.",
            icon: (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
            )
        },
        {
            title: "Regulatory Readiness",
            desc: "Pre-configured templates for ISO, FDA, and local compliance standards, ready for instant export.",
            icon: (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
            )
        },
        {
            title: "One-Click Audit",
            desc: "Generate comprehensive history reports for any batch or product in under 3 seconds.",
            icon: (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
            )
        }
    ];

    return (
        <section id="audit" className="py-16 bg-white overflow-hidden">
            <div className="max-w-7xl mx-auto px-6">
                <div className="grid lg:grid-cols-2 gap-16 items-center">
                    {/* Audit Verification Mockup - Shifted to Left */}
                    <div className={`order-2 lg:order-1 relative transition-all duration-1000 delay-300 ${animate ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
                        <div className="bg-white rounded-[2.5rem] border border-slate-200 shadow-2xl p-6 sm:p-10 relative z-10 overflow-hidden min-h-[450px]">
                            <div className="flex justify-between items-start mb-8">
                                <div>
                                    <div className="text-[10px] font-black text-sky-600 uppercase tracking-widest mb-1">Compliance Report</div>
                                    <div className="text-xl font-bold text-slate-900">Batch #RX-2024-001</div>
                                </div>
                                <div className="bg-sky-50 text-sky-700 px-3 py-1 rounded-full text-[10px] font-black uppercase border border-sky-100">
                                    Verified 100%
                                </div>
                            </div>

                            <div className="space-y-4">
                                {[
                                    { time: '10:24 AM', action: 'Raw Material Received', user: 'System', status: 'Success' },
                                    { time: '01:45 PM', action: 'Quality Buffer Check', user: 'Admin', status: 'Passed' },
                                    { time: '04:12 PM', action: 'Production Finalized', user: 'Operator', status: 'Signed' },
                                    { time: 'Just Now', action: 'Audit Log Exported', user: 'System', status: 'Encrypted' },
                                ].map((log, i) => (
                                    <div key={i} className={`p-4 rounded-xl border border-slate-100 bg-slate-50/50 flex justify-between items-center transition-all hover:bg-slate-50`}>
                                        <div className="flex gap-4 items-center">
                                            <div className="text-[10px] font-medium text-slate-400 font-mono">{log.time}</div>
                                            <div className="text-sm font-bold text-slate-700">{log.action}</div>
                                        </div>
                                        <div className="hidden sm:block text-[10px] font-bold text-sky-600 uppercase tracking-wider">{log.status}</div>
                                    </div>
                                ))}
                            </div>

                            <div className="mt-8 pt-8 border-t border-slate-100">
                                <div className="flex items-center gap-3 text-sky-600 mb-4">
                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                    </svg>
                                    <span className="text-sm font-black uppercase tracking-widest">End-to-End Encryption Active</span>
                                </div>
                                <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                                    <div className="h-full bg-sky-500 w-[100%]"></div>
                                </div>
                            </div>
                        </div>

                        {/* Background accents */}
                        <div className="absolute -top-10 -left-10 w-64 h-64 bg-sky-100 rounded-full blur-[80px] -z-10 opacity-30"></div>
                        <div className="absolute -bottom-10 -right-10 w-64 h-64 bg-indigo-100 rounded-full blur-[80px] -z-10 opacity-30"></div>
                    </div>

                    {/* Explanation - Shifted to Right */}
                    <div className={`order-1 lg:order-2 transition-all duration-1000 ${animate ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-50 border border-slate-200 shadow-sm mb-6">
                            <span className="flex h-2 w-2">
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-sky-500"></span>
                            </span>
                            <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Audit-Ready 24/7</span>
                        </div>

                        <h2 className="heading-lg mb-6 leading-tight">
                            Enhanced Audit: <br />
                            <span className="gradient-text font-black">Zero-Effort Compliance</span>
                        </h2>

                        <p className="text-p mb-10 text-slate-600 max-w-xl">
                            Eliminate the stress of manual auditing. OmniTrace maintains a perfect, tamper-proof record of your entire supply chain, ensuring you're always prepared for inspections.
                        </p>

                        <div className="space-y-6">
                            {auditFeatures.map((item, i) => (
                                <div key={i} className="flex gap-4 group cursor-default">
                                    <div className="w-12 h-12 shrink-0 rounded-2xl bg-gradient-to-br from-indigo-600 to-sky-500 text-white border-transparent shadow-lg shadow-sky-200 flex items-center justify-center transition-all duration-300 group-hover:scale-110">
                                        {item.icon}
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-slate-900 group-hover:text-sky-600 transition-colors">{item.title}</h4>
                                        <p className="text-sm text-slate-500 leading-relaxed">{item.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Audit;
